import { DeleteFive, Edit } from '@icon-park/react'
import { RefreshCw } from 'lucide-react'
import { memo, useEffect, useRef, useState } from 'react'

import { SearchResponse, ThemeResponse, UpdateThemeRequest } from '@shared/models'

import { useRouting } from '@/hooks'
import { Alert, Api, Confirmation } from '@/modules'

import { Button, Flex, IconTooltip, SelectedList, Switch, TableImage } from '@/components'
import { TagsList, TagsListItem } from '@/components/core'
import { ColumnType, Table } from '@/components/derived'

import { Tokens } from '@/assets/tokens'
import { Item, useSelectedList } from '@/components/core/SelectedList/SelectedList.hooks'
import { Str } from '@/utils/strings'
import { useCompanyContext } from '../CompanyDetails.context'
import { FiltersContainer, ShowActiveThemesWrapper, ThemeTableContainer } from './Themes.styles'
import { EditThemeTermsModal } from './components/EditThemeTermsModal'

export const ThemesTable = memo(Component)

function Component(): JSX.Element | null {
  const { companyID, currentPage, changeQueryParam } = useRouting()
  const { isLoadingThemes, themes, reloadThemes, themeFilters, setThemeFilters } = useCompanyContext()

  const [selectedThemeForEdit, setSelectedThemeForEdit] = useState<ThemeResponse | null>(null)
  const [showEditModal, setShowEditModal] = useState(false)

  const activeThemesBinder = Switch.Hook(themeFilters.isActive ?? true)
  const prevFiltersRef = useRef({
    brands: '',
    themeGroups: '',
    isActive: themeFilters.isActive ?? true,
  })
  const isAdjustingPageRef = useRef(false)

  const { sendAlert } = Alert.Hook()
  const { open: openConfirmation } = Confirmation.Hook({
    icon: DeleteFive,
    title: 'Você tem certeza?',
    text: 'Deseja desativar o tema?',
    confirmText: 'Sim! Desativar tema.',
  })

  const { binder: brandsFilterBinder, selectedOptions: selectedBrandsForFilter } = useSelectedList(
    (param: string): void => {
      loadBrands({ name: param })
    },
  )
  const { binder: themeGroupsFilterBinder, selectedOptions: selectedThemeGroupsForFilter } = useSelectedList(
    (param: string): void => {
      loadThemeGroups({ name: param })
    },
  )

  const [isLoadingBrands, loadBrands, brands] = Api.Query<SearchResponse[]>(
    `brands/search?company=${companyID}&size=10000`,
  )
  const [isLoadingThemeGroups, loadThemeGroups, themeGroups] = Api.Query<SearchResponse[]>(
    `theme-groups/search?company=${companyID}&size=10000`,
  )
  const { isRunning: isUpdatingTheme, run: updateTheme } = Api.Command<UpdateThemeRequest>(
    Api.Methods.PATCH,
    'themes/:id',
  )

  const selectedBrandsParsed = selectedBrandsForFilter.map((item: Item) => item.value).join(',')
  const selectedThemeGroupsParsed = selectedThemeGroupsForFilter.map((item: Item) => item.value).join(',')

  useEffect(() => {
    const filtersChanged =
      prevFiltersRef.current.brands !== selectedBrandsParsed ||
      prevFiltersRef.current.themeGroups !== selectedThemeGroupsParsed ||
      prevFiltersRef.current.isActive !== activeThemesBinder.isChecked

    prevFiltersRef.current = {
      brands: selectedBrandsParsed,
      themeGroups: selectedThemeGroupsParsed,
      isActive: activeThemesBinder.isChecked,
    }

    if (filtersChanged && currentPage !== '1') {
      isAdjustingPageRef.current = true
      changeQueryParam({
        param: 'pagina',
        value: '1',
      })
      return
    }

    if (isAdjustingPageRef.current) {
      isAdjustingPageRef.current = false
    }

    const filters = {
      brands: selectedBrandsParsed || undefined,
      themeGroups: selectedThemeGroupsParsed || undefined,
      isActive: activeThemesBinder.isChecked,
    }

    setThemeFilters(filters)

    reloadThemes({
      page: currentPage,
      ...filters,
    })
  }, [isUpdatingTheme, currentPage, selectedBrandsParsed, selectedThemeGroupsParsed, activeThemesBinder.isChecked])

  function handleRefresh() {
    reloadThemes({
      page: currentPage,
      brands: selectedBrandsParsed || undefined,
      themeGroups: selectedThemeGroupsParsed || undefined,
      isActive: activeThemesBinder.isChecked,
    })
  }

  function adjustPageIfNeeded(totalAfterAction: number, pageSize: number = 10): boolean {
    const currentPageNum = Number(currentPage)
    const totalPages = Math.ceil(totalAfterAction / pageSize)

    if (currentPageNum > totalPages && totalPages > 0) {
      isAdjustingPageRef.current = true
      changeQueryParam({
        param: 'pagina',
        value: String(totalPages),
      })
      return true
    }
    return false
  }

  function onConfirmationAccepted(id: string, isActive: boolean): void {
    if (isActive === undefined) return
    const input = {
      isActive,
      companyID,
    } as UpdateThemeRequest

    updateTheme({
      input,
      binds: [['id', id!]],
      onSuccess: () => onSuccess(isActive),
    })
  }

  function onSuccess(newState: boolean): void {
    sendAlert({
      type: 'success',
      title: 'Tema atualizado',
      message: 'Tema atualizado com sucesso',
    })

    const isActiveFilter = activeThemesBinder.isChecked
    const willRemoveFromList = (isActiveFilter && !newState) || (!isActiveFilter && newState)
    let pageWasAdjusted = false

    if (willRemoveFromList && themes?.total) {
      pageWasAdjusted = adjustPageIfNeeded(themes.total - 1)
    }

    if (!pageWasAdjusted) {
      reloadThemes({
        page: currentPage,
        brands: selectedBrandsParsed || undefined,
        themeGroups: selectedThemeGroupsParsed || undefined,
        isActive: activeThemesBinder.isChecked,
      })
    }
  }

  function handleEditThemeTerms(theme: ThemeResponse): void {
    setSelectedThemeForEdit(theme)
    setShowEditModal(true)
  }

  const columns: ColumnType<ThemeResponse>[] = [
    {
      key: 'brandName',
      title: 'Marca',
      width: 80,
      align: 'center',
      render: (_, theme) => (
        <div
          title={theme.brandName}
          style={{
            minHeight: '60px',
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'center',
            padding: '8px',
          }}>
          <TableImage id={theme.brandLogo} />
        </div>
      ),
    },
    {
      key: 'value',
      title: 'Nome',
      width: 240,
      align: 'center',
      render: (_, theme) => (
        <div style={{ padding: '12px 16px', textAlign: 'center' }}>
          <span style={{ fontSize: '14px', fontWeight: '500' }}>{theme.value}</span>
        </div>
      ),
    },
    {
      key: 'terms',
      title: 'Termos',
      render: (_, theme) => {
        const tags: TagsListItem[] =
          theme.terms ?
            theme.terms
              .split(';')
              .map((term, index) => ({
                id: `${theme.id}-${index}`,
                text: term.trim(),
              }))
              .filter((tag) => tag.text !== '')
          : []

        const allTermsText = tags.map((tag) => tag.text).join('; ')

        return (
          <div
            style={{
              padding: '12px 16px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '12px',
              minHeight: '60px',
            }}>
            <div
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
              }}
              title={allTermsText || 'Sem termos cadastrados'}>
              <TagsList tags={tags} maxVisible={8} variant="compact" emptyText="Sem termos" />
            </div>
            <Button.Root
              $variant="link"
              style={{
                color: Tokens.ColorUIPrimary,
                minWidth: '24px',
                height: '24px',
                padding: 0,
              }}
              onClick={() => handleEditThemeTerms(theme)}
              title="Editar termos">
              <Button.Icon value={Edit} />
            </Button.Root>
          </div>
        )
      },
    },
    {
      key: 'isStrictDeterministic',
      title: 'Determinístico',
      width: 120,
      align: 'center',
      render: (_, theme) => (
        <div style={{ padding: '12px 16px', textAlign: 'center' }}>
          <span
            style={{
              fontSize: '12px',
              fontWeight: '500',
              padding: '4px 8px',
              borderRadius: '4px',
              backgroundColor: theme.isStrictDeterministic ? Tokens.ColorUISuccessLight : Tokens.ColorBackground,
              color: theme.isStrictDeterministic ? Tokens.ColorUISuccess : Tokens.ColorTextSecondary,
            }}>
            {theme.isStrictDeterministic ? 'Sim' : 'Não'}
          </span>
        </div>
      ),
    },
    {
      key: 'options',
      title: (
        <Flex $type="row" $align="center" $justify="center" $gap={8}>
          <span
            style={{
              fontWeight: 600,
              fontSize: '16px',
              color: Tokens.ColorTextPrimary,
              textTransform: 'capitalize',
              letterSpacing: '0.5px',
            }}>
            Ativo
          </span>
          <Button.Root onClick={handleRefresh} $variant="link">
            <IconTooltip
              tooltipText="Atualizar listagem"
              icon={<RefreshCw size={16} color={Tokens.ColorTextSecondary} />}
            />
          </Button.Root>
        </Flex>
      ),
      width: 120,
      align: 'center',
      render: (_, theme) => {
        const isDefaultTheme = theme.value === 'SEM TEMA'

        return (
          <div style={{ padding: '12px 16px', textAlign: 'center' }}>
            <Switch.Root
              isChecked={theme.isActive ?? false}
              disabled={isDefaultTheme}
              toggle={() => {
                if (isDefaultTheme) {
                  sendAlert({
                    type: 'error',
                    title: 'Ação não permitida',
                    message: 'O tema "SEM TEMA" não pode ser desativado',
                  })
                  return
                }

                theme.isActive ?
                  openConfirmation(() => onConfirmationAccepted(theme.id, !theme.isActive))
                : onConfirmationAccepted(theme.id, !theme.isActive)
              }}
            />
          </div>
        )
      },
    },
  ]

  return (
    <>
      <ThemeTableContainer>
        {(brands && brands.length > 0) || (themeGroups && themeGroups.length > 0) ?
          <FiltersContainer>
            {brands && brands.length > 0 && (
              <SelectedList
                placeholder="Marcas"
                isLoading={isLoadingBrands}
                options={
                  brands?.map(
                    (item: SearchResponse): Item => ({ text: Str.from(item.text).initCap, value: item.value }),
                  ) ?? []
                }
                {...brandsFilterBinder}
              />
            )}

            {themeGroups && themeGroups.length > 0 && (
              <SelectedList
                placeholder="Grupos"
                isLoading={isLoadingThemeGroups}
                options={
                  themeGroups?.map(
                    (item: SearchResponse): Item => ({ text: Str.from(item.text).initCap, value: item.value }),
                  ) ?? []
                }
                {...themeGroupsFilterBinder}
              />
            )}

            <ShowActiveThemesWrapper>
              <Switch.Root label="Mostrar apenas temas ativos" {...activeThemesBinder} direction="row" />
            </ShowActiveThemesWrapper>
          </FiltersContainer>
        : <ShowActiveThemesWrapper>
            <Switch.Root label="Mostrar apenas temas ativos" {...activeThemesBinder} direction="row" />
          </ShowActiveThemesWrapper>
        }

        <Table isLoading={isLoadingThemes} columns={columns} data={themes?.elements} totalPages={themes?.pages} />
      </ThemeTableContainer>

      {showEditModal && selectedThemeForEdit && (
        <EditThemeTermsModal
          theme={selectedThemeForEdit}
          onClose={() => {
            setShowEditModal(false)
            setSelectedThemeForEdit(null)
          }}
          onSuccess={() => {
            setShowEditModal(false)
            setSelectedThemeForEdit(null)
            reloadThemes({
              page: currentPage,
              brands: selectedBrandsParsed || undefined,
              themeGroups: selectedThemeGroupsParsed || undefined,
              isActive: activeThemesBinder.isChecked,
            })
          }}
        />
      )}
    </>
  )
}
